import {
  Accordion,
  Button,
  ButtonVariant,
  ColorClass,
  Icon,
  IconIdentifier,
  Input,
  SwitchCard,
  toast,
  Tooltip,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { filter, isEmpty, isNil } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateDashboardCategoryEntry,
  useUpdateDashboardCategoryEntry,
} from 'shared/api';
import { ConfirmationModal } from 'shared/components';
import { KEYBOARD_KEY_HINTS } from 'shared/constants';
import { ErrorSlugs, KeyboardKey } from 'shared/enums';
import {
  type DashboardConfigurationAccordionProps,
  type DashboardEntry,
  type EditingEntry,
} from './types';

const getInitialAccordionEntries = (
  entries: DashboardEntry[],
): DashboardEntry[] =>
  [...entries].sort(
    (firstEntry, secondEntry) =>
      Number(secondEntry.isActive) - Number(firstEntry.isActive),
  );

export const DashboardConfigurationAccordion: React.FC<
  DashboardConfigurationAccordionProps
> = ({
  fieldKey,
  title,
  entries,
  isAccordionActive,
  onToggleAccordionItem,
}) => {
  // States.
  const [accordionEntries, setAccordionEntries] = useState<DashboardEntry[]>(
    getInitialAccordionEntries(entries),
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [newEntryLabel, setNewEntryLabel] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingEntry, setEditingEntry] = useState<EditingEntry>({
    id: null,
    label: '',
  });
  const [pendingDeactivationEntry, setPendingDeactivationEntry] =
    useState<DashboardEntry | null>(null);

  // Refs.
  const newEntryInputRef = useRef<HTMLInputElement | null>(null);
  const editInputRef = useRef<HTMLInputElement | null>(null);
  const newEntryContainerRef = useRef<HTMLDivElement | null>(null);
  const editContainerRef = useRef<HTMLDivElement | null>(null);

  // Apis.
  const {
    data: createdEntryResponse,
    mutate: createDashboardCategoryEntry,
    isError: isCreatingEntryError,
    isSuccess: isCreatingEntrySuccess,
    error: createEntryError,
  } = useCreateDashboardCategoryEntry();

  const {
    data: updatedEntryResponse,
    mutate: updateDashboardCategoryEntry,
    isError: isUpdatingEntryError,
    isSuccess: isUpdatingEntrySuccess,
    error: updateEntryError,
  } = useUpdateDashboardCategoryEntry();

  // Effects.
  useEffect(() => {
    if (!isAccordionActive) {
      resetAccordionState();
    }
  }, [isAccordionActive]);

  useEffect(() => {
    if (isCreatingEntrySuccess) {
      const { id, name, isActive } = createdEntryResponse.data;

      const createdEntry: DashboardEntry = {
        id,
        name,
        isActive,
      };

      setAccordionEntries((prev) => [createdEntry, ...prev]);

      toast.success(`${name} created successfully.`);

      resetAccordionState();
    }

    if (isCreatingEntryError) {
      if (
        createEntryError.meta.details?.form['slug'] ===
        ErrorSlugs.ObjectAlreadyExists
      ) {
        toast.error(
          `${newEntryLabel} already exists. Please try a different name.`,
        );
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  }, [isCreatingEntrySuccess, isCreatingEntryError]);

  useEffect(() => {
    if (isUpdatingEntrySuccess) {
      const { id, name, isActive } = updatedEntryResponse.data;

      setAccordionEntries((prev) =>
        prev.map((entry) =>
          entry.id === id
            ? {
                ...entry,
                name,
                isActive,
              }
            : entry,
        ),
      );

      if (!isActive) {
        toast.success(`${name} deactivated successfully.`);
      }

      resetEditingState();
      handleCloseDeactivateModal();
    }

    if (isUpdatingEntryError) {
      if (
        updateEntryError.meta.details?.form['slug'] ===
        ErrorSlugs.ObjectAlreadyExists
      ) {
        toast.error(
          `${editingEntry.label} already exists. Please try a different name.`,
        );
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  }, [isUpdatingEntrySuccess, isUpdatingEntryError]);

  // Exit edit mode (discard) when clicking outside the active input, mirroring Esc.
  useEffect(() => {
    const isEditing = isAddingNew || !isNil(editingEntry.id);

    if (!isEditing) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        isAddingNew &&
        !newEntryContainerRef.current?.contains(target)
      ) {
        resetNewEntryState();
      }

      if (
        !isNil(editingEntry.id) &&
        !editContainerRef.current?.contains(target)
      ) {
        resetEditingState();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAddingNew, editingEntry.id]);

  // Memos.
  const filteredEntries = useMemo(() => {
    return filter(accordionEntries, ({ name }) =>
      name.trim().toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [accordionEntries, searchTerm]);

  // Handlers
  const handleAddNewEntry = () => {
    setIsAddingNew(true);
  };

  const resetEditingState = () => {
    setEditingEntry({
      id: null,
      label: '',
    });
  };

  const resetNewEntryState = () => {
    setIsAddingNew(false);
    setNewEntryLabel('');
  };

  const resetAccordionState = () => {
    setSearchTerm('');
    setPendingDeactivationEntry(null);
    resetNewEntryState();
    resetEditingState();
  };

  const handleDeactivateRequest = (entry: DashboardEntry) => {
    if (entry.isActive) {
      setPendingDeactivationEntry(entry);
      return;
    }

    handleToggleActive(entry);
  };

  const handleCloseDeactivateModal = () => {
    setPendingDeactivationEntry(null);
  };

  const handleConfirmDeactivate = () => {
    if (isNil(pendingDeactivationEntry)) {
      return;
    }

    handleToggleActive(pendingDeactivationEntry);
    handleCloseDeactivateModal();
  };

  const handleSaveNewEntry = () => {
    const trimmedValue = newEntryLabel.trim();

    if (isEmpty(trimmedValue)) {
      return;
    }

    createDashboardCategoryEntry({
      category: fieldKey,
      name: trimmedValue,
    });
  };

  const handleStartEdit = (entry: DashboardEntry) => {
    setEditingEntry({
      id: entry.id,
      label: entry.name,
    });
  };

  const handleSaveEdit = (entryId: string) => {
    const trimmedValue = editingEntry.label.trim();

    if (isEmpty(trimmedValue)) {
      return;
    }

    updateDashboardCategoryEntry({
      id: entryId,
      category: fieldKey,
      name: trimmedValue,
    });
  };

  const handleToggleActive = (entry: DashboardEntry) => {
    updateDashboardCategoryEntry({
      id: entry.id,
      category: fieldKey,
      name: entry.name,
      isActive: !entry.isActive,
    });
  };

  const focusNewEntryInput = () => {
    newEntryInputRef.current?.focus();
  };

  const focusEditInput = () => {
    editInputRef.current?.focus();
  };

  const handleNewEntryInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === KeyboardKey.Enter) {
      handleSaveNewEntry();
    }

    if (event.key === KeyboardKey.Escape) {
      event.stopPropagation();
      resetNewEntryState();
    }
  };

  const handleEditEntryInputKeyDown =
    (entryId: string) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();

      if (event.key === KeyboardKey.Enter) {
        handleSaveEdit(entryId);
      }

      if (event.key === KeyboardKey.Escape) {
        resetEditingState();
      }
    };

  // Variables.
  const newEntryContainerClassName = classNames(
    'dashboard-configuration__accordion__new-entry',
    {
      'dashboard-configuration__accordion__new-entry--empty-list':
        isEmpty(filteredEntries),
    },
  );

  // Renderers.
  const renderAccordionHeader = () => {
    return (
      <div
        className="dashboard-configuration__accordion__header"
        onClick={onToggleAccordionItem}
      >
        <div className="dashboard-configuration__accordion__title">{title}</div>

        <div className="dashboard-configuration__accordion__subtitle">
          {accordionEntries.length}{' '}
          {accordionEntries.length === 1 ? 'entry' : 'entries'}
        </div>
      </div>
    );
  };

  return (
    <Accordion.Item
      eventKey={fieldKey}
      title={renderAccordionHeader()}
      iconSize={14}
    >
      {isEmpty(accordionEntries) && !isAddingNew ? (
        <div className="dashboard-configuration__accordion__empty-list">
          <Icon
            identifier={IconIdentifier.InfoCircle}
            size={20}
            colorClass={ColorClass.Neutral200}
          />

          <div className="dashboard-configuration__accordion__empty-list-text">
            No entries available for {title}.
          </div>

          <Button
            variant={ButtonVariant.Link}
            rightIconIdentifier={IconIdentifier.Plus}
            iconSize={14}
            onClick={handleAddNewEntry}
            disabled={isAddingNew}
          >
            Add New Entry
          </Button>
        </div>
      ) : (
        <>
          {!isEmpty(accordionEntries) && (
            <div className="dashboard-configuration__accordion__actions">
              <Input.Text
                placeholder="Search"
                value={searchTerm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.target.value)
                }
              />

              <Button
                variant={ButtonVariant.Link}
                rightIconIdentifier={IconIdentifier.Plus}
                iconSize={14}
                onClick={handleAddNewEntry}
                disabled={isAddingNew || !isNil(editingEntry.id)}
              >
                Add New Entry
              </Button>
            </div>
          )}

          {isAddingNew && (
            <div
              ref={newEntryContainerRef}
              className={newEntryContainerClassName}
              onClick={focusNewEntryInput}
            >
              <Input.Text
                autoFocus
                value={newEntryLabel}
                ref={newEntryInputRef}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setNewEntryLabel(event.target.value)
                }
                onKeyDown={handleNewEntryInputKeyDown}
                className="dashboard-configuration__accordion__input-text"
              />

              <span>
                {isEmpty(newEntryLabel)
                  ? KEYBOARD_KEY_HINTS.Discard
                  : KEYBOARD_KEY_HINTS.Save}
              </span>
            </div>
          )}

          <div className="dashboard-configuration__accordion__list">
            {filteredEntries.map((entry: DashboardEntry) => (
              <div
                key={entry.id}
                ref={
                  editingEntry.id === entry.id ? editContainerRef : undefined
                }
                className={classNames(
                  'dashboard-configuration__accordion__list-item',
                  {
                    'dashboard-configuration__accordion__list-item--inactive':
                      !entry.isActive,
                  },
                )}
                onClick={focusEditInput}
              >
                <div className="dashboard-configuration__accordion__list-item-label">
                  {editingEntry.id === entry.id ? (
                    <Input.Text
                      autoFocus
                      ref={editInputRef}
                      value={editingEntry.label}
                      className="dashboard-configuration__accordion__input-text"
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setEditingEntry((prev) => ({
                          ...prev,
                          label: event.target.value,
                        }))
                      }
                      onKeyDown={handleEditEntryInputKeyDown(entry.id)}
                    />
                  ) : (
                    <span>{entry.name}</span>
                  )}
                </div>

                <div className="dashboard-configuration__accordion__list-item-actions">
                  {entry.isActive && editingEntry.id !== entry.id && (
                    <Tooltip hoverText="Edit">
                      <Icon
                        identifier={IconIdentifier.Pencil}
                        onClick={() => handleStartEdit(entry)}
                        size={14}
                        colorClass={ColorClass.Primary500}
                      />
                    </Tooltip>
                  )}

                  {editingEntry.id !== entry.id ? (
                    <Tooltip
                      hoverText={entry.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <SwitchCard
                        className="dashboard-configuration__accordion__switch-card"
                        title=""
                        checked={entry.isActive}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          handleDeactivateRequest(entry);
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <span className="dashboard-configuration__accordion__edit-action">
                      {isEmpty(editingEntry.label)
                        ? KEYBOARD_KEY_HINTS.Discard
                        : KEYBOARD_KEY_HINTS.Save}
                    </span>
                  )}
                </div>
              </div>
            ))}

            {isEmpty(filteredEntries) && !isAddingNew && (
              <div className="dashboard-configuration__accordion__empty">
                No entries found for {title}.
              </div>
            )}
          </div>

          {/* Deactivate Modal */}
          {pendingDeactivationEntry && (
            <ConfirmationModal
              title="Deactivate Custom Entry"
              message={
                <>
                  Are you sure you want to deactivate{' '}
                  <strong>{pendingDeactivationEntry.name}</strong>?
                </>
              }
              confirmText="Yes, Deactivate"
              isConfirmDanger
              onClose={handleCloseDeactivateModal}
              onSubmit={handleConfirmDeactivate}
            />
          )}
        </>
      )}
    </Accordion.Item>
  );
};
